export function generateContrastColor(
  customColor: string,
  mode: "light" | "dark" = "light",
  lightnessFactor: number = mode === "light" ? 0.92 : 0.15
): string {
  // Convert hex to RGB
  const hex = customColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert RGB to HSL
  const rNormalized = r / 255;
  const gNormalized = g / 255;
  const bNormalized = b / 255;

  const max = Math.max(rNormalized, gNormalized, bNormalized);
  const min = Math.min(rNormalized, gNormalized, bNormalized);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rNormalized:
        h =
          (gNormalized - bNormalized) / d + (gNormalized < bNormalized ? 6 : 0);
        break;
      case gNormalized:
        h = (bNormalized - rNormalized) / d + 2;
        break;
      case bNormalized:
        h = (rNormalized - gNormalized) / d + 4;
        break;
    }
    h /= 6;
  }

  // Adjust lightness while keeping hue and saturation
  const newL =
    mode === "light"
      ? Math.min(1, l + (1 - l) * lightnessFactor) // Make it lighter
      : Math.max(0, l * lightnessFactor); // Make it darker

  // Convert back to RGB
  function hue2rgb(p: number, q: number, t: number) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  const q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
  const p = 2 * newL - q;

  const newR = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const newG = Math.round(hue2rgb(p, q, h) * 255);
  const newB = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

  // Convert back to hex
  return (
    "#" +
    newR.toString(16).padStart(2, "0") +
    newG.toString(16).padStart(2, "0") +
    newB.toString(16).padStart(2, "0")
  );
}

export const COLORS = {
  background: {
    light: "#ffffff",
    dark: "#05050a",
  },
  expense: {
    main: "#fb923c",
    dark: "#f97316",
    light: "#fdba74",
  },
  income: {
    main: "#60a5fa",
    dark: "#3b82f6",
    light: "#93c5fd",
  },
  debts: {
    current: "#4caf50",
    thirty: "#ffdb58",
    sixty: "#ff9800",
    ninety: "#ff5722",
    ninetyPlus: "#f44336",
  },
  paper: {
    light: "#f5f5f5",
    dark: "#05050a",
  },
  primary: {
    main: "#2563eb",
    dark: "#1d4ed8",
    light: "#60a5fa",
    lighter: "#bfdbfe",
    darker: "#1e40af",
  },
  secondary: {
    main: "#fb923c",
    dark: "#f97316",
    light: "#fdba74",
    lighter: "#fed7aa",
    darker: "#ea580c",
  },
  success: {
    border: "#15803d",
    text: "#15803d",
    background: "#dcfce7",
  },
  error: {
    border: "#dc2626",
    text: "#dc2626",
    background: "#fee2e2",
  },
  hover: {
    royal: "#1976D2",
    light: "rgba(25, 118, 210, 0.08)",
  },
  border: {
    light: "#d1d5db",
    dark: "#404040",
  },
  grey: {
    dark: {
      100: "#2d2d2d",
      200: "#333333",
      300: "#404040",
      400: "#666666",
    },
    light: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
    },
  },
} as const;
